import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { GetNotificationsButton } from '@/components/notifications';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { Notification } from '@/lib/notifications';
import { decodeHtmlEntities, urlSanitize } from '../-utils';
import { PriceScale } from './price-scale';

const BASE_URL = 'https://images.pricedrop.co.il';

interface ItemCardProps {
  name: string;
  uin: number;
  uinsql: string;
  brandName: string;
  price: number | null;
  highestPrice: number;
  lowestPrice: number;
  meanPrice: number;
  previousPrice: number;
  lazyLoad?: boolean;
  notification?: Notification | null;
}

export function ItemCard({
  name,
  uin,
  uinsql,
  brandName,
  price,
  highestPrice,
  lowestPrice,
  meanPrice,
  previousPrice,
  lazyLoad = false,
  notification,
}: ItemCardProps) {
  const [loaded, setLoaded] = useState(false);
  const outlet = uinsql[0] === 'M';
  const itemName = outlet
    ? name.replace('מציאון ועודפים - ', '').replace('מציאון ועודפים', '')
    : name;

  const priceTitle = price == null ? `${highestPrice} - ${lowestPrice}` : price;
  const percent = (((previousPrice - (price ?? 0)) / previousPrice) * 100).toFixed(2);
  const itemUrl = `/item/${uin}/${urlSanitize(name)}`;

  return (
    <Card className="group/card relative flex flex-row overflow-hidden sm:flex-col bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-800/60 dark:to-gray-700/60 border border-slate-200 dark:border-gray-600/50 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/10 dark:hover:shadow-blue-400/20 hover:border-blue-300 dark:hover:border-blue-500/50 hover:-translate-y-0.5 item-card py-0">
      <div className="absolute top-2 start-2 z-10 flex flex-col items-start space-y-1 sm:start-1">
        {outlet && (
          <Badge className="bg-gradient-to-r from-orange-500 to-orange-600 font-bold text-white shadow-sm border-0 transition-all duration-200 hover:shadow-md hover:shadow-orange-500/20">
            מציאון ועודפים
          </Badge>
        )}
        {price == null && (
          <Badge className="bg-gradient-to-r from-red-600 to-red-700 dark:from-red-500 dark:to-red-600 font-bold text-white shadow-sm border-0 transition-all duration-200 hover:shadow-md hover:shadow-red-500/20">
            לא במלאי
          </Badge>
        )}
        {price !== null && price < previousPrice && (
          <Badge className="bg-gradient-to-r from-emerald-400 to-emerald-500 dark:from-emerald-500 dark:to-emerald-600 text-center font-bold text-white shadow-sm border-0 transition-all duration-200 hover:shadow-md hover:shadow-emerald-500/20">
            הנחה {percent}%
          </Badge>
        )}
        {price === lowestPrice && lowestPrice !== highestPrice && (
          <Badge className="bg-gradient-to-r from-pink-500 to-pink-600 dark:from-pink-400 dark:to-pink-500 font-bold text-white shadow-sm border-0 transition-all duration-200 hover:shadow-md hover:shadow-pink-500/20">
            הכי זול שהיה
          </Badge>
        )}
      </div>
      <div className="w-1/3 shrink-0 sm:w-auto">
        <CardContent className="flex h-full items-center justify-center bg-gradient-to-br from-white to-slate-50 px-0 py-2 sm:border-b border-slate-200/50">
          <Link to={itemUrl} aria-label={`מעקב מחירים על ${name}`}>
            <img
              onLoad={() => setLoaded(true)}
              loading={lazyLoad ? 'lazy' : 'eager'}
              fetchPriority={lazyLoad ? 'low' : 'high'}
              alt={`תמונה של ${name}`}
              className={`max-h-[120px] w-auto object-contain sm:h-[200px] sm:w-[200px] sm:max-h-none transform transition duration-300 ease-in group-hover/card:scale-105 ${
                lazyLoad ? (loaded ? 'opacity-100' : 'opacity-0') : ''
              }`}
              height="200"
              width="200"
              src={`${BASE_URL}/items/${uin}.webp`}
            />
          </Link>
        </CardContent>
      </div>
      <div className="mt-1 mb-2 flex min-w-0 flex-1 flex-col sm:w-auto sm:flex-initial">
        <CardFooter className="p-0 px-3 font-semibold">
          <div
            className={`text-[20px] ${price == null ? 'line-through' : ''} text-slate-800 dark:text-gray-200`}
          >
            <span className="text-[18px]">₪</span>
            {priceTitle}
          </div>
        </CardFooter>
        <CardHeader className="group/title p-0 px-3 pt-0">
          <CardDescription className="h-[20px] text-end sm:group-hover/title:hidden text-slate-600 dark:text-gray-400">
            {brandName}
          </CardDescription>
          <CardTitle className="text-elipsis line-clamp-2 h-[40px] text-[14px] sm:group-hover/title:line-clamp-3 sm:group-hover/title:h-[66px] sm:hover:line-clamp-none text-slate-700 dark:text-gray-300">
            <Link
              to={itemUrl}
              className="transition-colors duration-200 hover:text-blue-600 dark:hover:text-blue-400"
            >
              {decodeHtmlEntities(itemName)}
            </Link>
          </CardTitle>
          <PriceScale
            price={price}
            highestPrice={highestPrice}
            lowestPrice={lowestPrice}
            meanPrice={meanPrice}
          />
          <div className="mt-auto flex w-full items-stretch gap-2 pb-1">
            <GetNotificationsButton
              itemUin={uin}
              itemName={name}
              currentPrice={price}
              isInStock={price !== null && price > 0}
              existingNotification={notification}
              compact
            />
            <Link to={itemUrl} className="flex-1">
              <Button className="h-full w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 border-0 text-white font-medium transition-all duration-300 hover:shadow-md hover:shadow-blue-500/20 hover:-translate-y-0.5">
                היסטוריית מחירים
              </Button>
            </Link>
          </div>
        </CardHeader>
      </div>
    </Card>
  );
}
